import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getDashboard, getSalesByPaymentMethod, getStores, recalculateAllBalances, removeUserStore, fixAdminStores, getStoreComparison, getSalesTrends } from '../api/api';
import { DollarSign, TrendingUp, TrendingDown, Wallet, Building2, PieChart, CreditCard, Banknote, Phone, Receipt, Coins, BarChart3, Filter, RefreshCw, AlertTriangle, UserX, ArrowUpRight, ArrowDownRight, Activity, ShoppingCart, Store, Hash, Award, Flame, LineChart } from 'lucide-react';
import { PieChart as RechartsPieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, LineChart as RechartsLineChart, Line, CartesianGrid, Legend } from 'recharts';
import CashierDashboard from './CashierDashboard';

const Dashboard = () => {
  const { user } = useAuth();
  
  if (['cashier', 'store_manager'].includes(user?.role)) {
    return <CashierDashboard />;
  }
  
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [salesReport, setSalesReport] = useState(null);
  const [stores, setStores] = useState([]);
  const [reportFilters, setReportFilters] = useState({
    store_id: '',
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1
  });
  const [recalculateResult, setRecalculateResult] = useState(null);
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [showRecalculateModal, setShowRecalculateModal] = useState(false);
  const [showFixStoresModal, setShowFixStoresModal] = useState(false);
  const [fixStoresResult, setFixStoresResult] = useState(null);
  const [isFixingStores, setIsFixingStores] = useState(false);
  // New states for comparison and trends
  const [storeComparison, setStoreComparison] = useState(null);
  const [comparisonPeriod, setComparisonPeriod] = useState('today');
  const [salesTrends, setSalesTrends] = useState(null);
  const [trendsPeriod, setTrendsPeriod] = useState('weekly');

  const PAYMENT_COLORS = {
    'POS': '#3b82f6',
    'Efectivo': '#10b981',
    'Recargas': '#f59e0b',
    'Recaudo': '#8b5cf6',
    'Sobrantes': '#ef4444'
  };

  const PAYMENT_ICONS = {
    'POS': CreditCard,
    'Efectivo': Banknote,
    'Recargas': Phone,
    'Recaudo': Receipt,
    'Sobrantes': Coins
  };

  useEffect(() => {
    loadDashboard();
    loadComparisonData();
    loadTrendsData();
    const interval = setInterval(loadDashboard, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (['admin', 'manager'].includes(user?.role)) {
      loadStores();
      loadSalesReport();
    }
  }, [user?.role]);

  useEffect(() => {
    if (['admin', 'manager'].includes(user?.role)) {
      loadSalesReport();
    }
  }, [reportFilters]);

  useEffect(() => {
    loadComparisonData();
  }, [comparisonPeriod]);

  useEffect(() => {
    loadTrendsData();
  }, [trendsPeriod]);

  const loadStores = async () => {
    try { setStores(await getStores()); } catch (e) { console.error(e); }
  };

  const loadComparisonData = async () => {
    try {
      const data = await getStoreComparison(comparisonPeriod);
      setStoreComparison(data);
    } catch (e) { console.error(e); }
  };

  const loadTrendsData = async () => {
    try {
      const data = await getSalesTrends(trendsPeriod);
      setSalesTrends(data);
    } catch (e) { console.error(e); }
  };

  const loadSalesReport = async () => {
    try {
      const data = await getSalesByPaymentMethod(reportFilters.store_id || null, reportFilters.year || null, reportFilters.month || null);
      setSalesReport(data);
    } catch (e) { console.error(e); }
  };

  const loadDashboard = async () => {
    try { setDashboard(await getDashboard()); } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const handleRecalculateBalances = async () => {
    setIsRecalculating(true); setRecalculateResult(null);
    try { const r = await recalculateAllBalances(); setRecalculateResult(r); await loadDashboard(); } catch (e) { setRecalculateResult({ error: e.response?.data?.detail || 'Error' }); } finally { setIsRecalculating(false); }
  };

  const handleFixAdminStores = async () => {
    setIsFixingStores(true); setFixStoresResult(null);
    try { setFixStoresResult(await fixAdminStores()); } catch (e) { setFixStoresResult({ error: e.response?.data?.detail || 'Error' }); } finally { setIsFixingStores(false); }
  };

  const handleRemoveAdminStore = async () => {
    if (!user?.id) return;
    try { await removeUserStore(user.id); window.location.reload(); } catch (e) { alert('Error: ' + (e.response?.data?.detail || e.message)); }
  };

  const isAdmin = ['admin', 'manager', 'supervisor'].includes(user?.role);
  const isAdminOrManager = ['admin', 'manager'].includes(user?.role);
  const isPurchaseManager = user?.role === 'purchase_manager';

  const getPieChartData = () => {
    if (!salesReport?.by_payment_method) return [];
    return Object.entries(salesReport.by_payment_method).filter(([_, d]) => d.total_amount > 0).map(([m, d]) => ({ name: m, value: d.total_amount }));
  };

  const getBarChartData = () => {
    if (!salesReport?.by_payment_method) return [];
    return Object.entries(salesReport.by_payment_method).map(([m, d]) => ({ name: m, total: d.total_amount, transacciones: d.transaction_count }));
  };

  // Calculate today's highlights from store data
  const getTodayHighlights = () => {
    if (!dashboard?.stores?.length) return { totalSalesToday: 0, totalExpensesToday: 0, totalTransactions: 0, topSalesStore: null, topExpenseStore: null };
    
    let totalSalesToday = 0, totalExpensesToday = 0;
    let topSalesStore = { name: '-', amount: 0 };
    let topExpenseStore = { name: '-', amount: 0 };
    
    dashboard.stores.forEach(s => {
      totalSalesToday += s.total_sales_today || 0;
      totalExpensesToday += s.total_expenses_today || 0;
      if ((s.total_sales_today || 0) > topSalesStore.amount) topSalesStore = { name: s.store_name, amount: s.total_sales_today, currency: s.currency };
      if ((s.total_expenses_today || 0) > topExpenseStore.amount) topExpenseStore = { name: s.store_name, amount: s.total_expenses_today, currency: s.currency };
    });
    
    return { totalSalesToday, totalExpensesToday, topSalesStore, topExpenseStore };
  };

  const months = [
    { value: 1, label: 'Ene' }, { value: 2, label: 'Feb' }, { value: 3, label: 'Mar' },
    { value: 4, label: 'Abr' }, { value: 5, label: 'May' }, { value: 6, label: 'Jun' },
    { value: 7, label: 'Jul' }, { value: 8, label: 'Ago' }, { value: 9, label: 'Sep' },
    { value: 10, label: 'Oct' }, { value: 11, label: 'Nov' }, { value: 12, label: 'Dic' }
  ];

  const monthsFull = ['', 'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-12 h-12 border-4 border-blue-100 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
          <p className="text-slate-500 text-sm">Cargando dashboard...</p>
        </div>
      </div>
    );
  }

  if (isPurchaseManager) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-2xl p-6 text-white">
          <p className="text-slate-400 text-sm">Bienvenido</p>
          <h1 className="text-2xl font-bold mt-1">{user?.name}</h1>
          <p className="text-slate-400 text-sm mt-1">Encargado de Compras</p>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <a href="/suppliers" className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all border border-slate-200 group">
            <Building2 className="text-blue-600 mb-3 group-hover:scale-110 transition-transform" size={28} />
            <p className="font-bold text-slate-800">Proveedores</p>
            <p className="text-sm text-slate-400">Gestionar proveedores</p>
          </a>
          <a href="/annual-reports" className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all border border-slate-200 group">
            <PieChart className="text-blue-600 mb-3 group-hover:scale-110 transition-transform" size={28} />
            <p className="font-bold text-slate-800">Reportes</p>
            <p className="text-sm text-slate-400">Ver reportes de compras</p>
          </a>
        </div>
      </div>
    );
  }

  const highlights = getTodayHighlights();

  return (
    <div className="space-y-6">
      {/* Modals */}
      {showFixStoresModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-lg w-full mx-4">
            <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center space-x-2"><UserX className="text-red-500" size={20} /><span>Corregir Tiendas Admin</span></h3>
            {!fixStoresResult && !isFixingStores && (
              <div className="space-y-4">
                <p className="text-sm text-slate-600 bg-amber-50 p-3 rounded-xl border border-amber-100">Quita la tienda asignada a todos los administradores.</p>
                <div className="flex space-x-3">
                  <button onClick={handleFixAdminStores} className="flex-1 py-2.5 bg-red-500 text-white rounded-xl hover:bg-red-600 font-medium text-sm">Corregir</button>
                  <button onClick={() => setShowFixStoresModal(false)} className="py-2.5 px-4 bg-slate-100 text-slate-600 rounded-xl text-sm">Cancelar</button>
                </div>
              </div>
            )}
            {isFixingStores && <div className="text-center py-8"><div className="w-8 h-8 border-3 border-red-200 border-t-red-500 rounded-full animate-spin mx-auto"></div></div>}
            {fixStoresResult && !fixStoresResult.error && (
              <div className="space-y-3">
                <p className="text-sm text-green-700 bg-green-50 p-3 rounded-xl border border-green-100">{fixStoresResult.message}</p>
                <button onClick={() => { setShowFixStoresModal(false); setFixStoresResult(null); window.location.reload(); }} className="w-full py-2.5 bg-blue-600 text-white rounded-xl font-medium text-sm">Cerrar</button>
              </div>
            )}
            {fixStoresResult?.error && (
              <div className="space-y-3">
                <p className="text-sm text-red-600 bg-red-50 p-3 rounded-xl">{fixStoresResult.error}</p>
                <button onClick={() => { setShowFixStoresModal(false); setFixStoresResult(null); }} className="w-full py-2.5 bg-slate-100 text-slate-600 rounded-xl text-sm">Cerrar</button>
              </div>
            )}
          </div>
        </div>
      )}

      {showRecalculateModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center space-x-2"><RefreshCw className={`text-blue-600 ${isRecalculating ? 'animate-spin' : ''}`} size={20} /><span>Recalcular Balances</span></h3>
            {!recalculateResult && !isRecalculating && (
              <div className="space-y-4">
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                  <p className="text-blue-700 text-sm">Recalcula el balance de todas las tiendas.</p>
                  <p className="text-blue-800 font-mono text-xs mt-1 bg-blue-100 p-2 rounded-lg">Balance = Saldo Inicial + Ventas - Gastos</p>
                </div>
                <div className="flex space-x-3">
                  <button onClick={handleRecalculateBalances} className="flex-1 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-medium text-sm">Recalcular</button>
                  <button onClick={() => setShowRecalculateModal(false)} className="py-2.5 px-4 bg-slate-100 text-slate-600 rounded-xl text-sm">Cancelar</button>
                </div>
              </div>
            )}
            {isRecalculating && <div className="text-center py-8"><div className="w-8 h-8 border-3 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto"></div></div>}
            {recalculateResult && !recalculateResult.error && (
              <div className="space-y-4">
                <p className="text-sm text-green-700 bg-green-50 p-3 rounded-xl border border-green-100">{recalculateResult.message} — Tiendas: {recalculateResult.total_stores} | Ajustadas: {recalculateResult.stores_adjusted}</p>
                {recalculateResult.stores_adjusted > 0 && recalculateResult.details && (
                  <table className="min-w-full text-sm"><thead className="bg-slate-50"><tr><th className="px-3 py-2 text-left text-xs font-medium text-slate-500">Tienda</th><th className="px-3 py-2 text-right text-xs font-medium text-slate-500">Antes</th><th className="px-3 py-2 text-right text-xs font-medium text-slate-500">Después</th><th className="px-3 py-2 text-right text-xs font-medium text-slate-500">Ajuste</th></tr></thead>
                  <tbody className="divide-y divide-slate-100">{recalculateResult.details.filter(s => s.adjustment !== 0).map(s => (
                    <tr key={s.store_name}><td className="px-3 py-2 text-slate-700">{s.store_name}</td><td className="px-3 py-2 text-right text-red-500">Q{s.old_balance.toFixed(2)}</td><td className="px-3 py-2 text-right text-green-600">Q{s.new_balance.toFixed(2)}</td><td className={`px-3 py-2 text-right font-medium ${s.adjustment > 0 ? 'text-green-600' : 'text-red-500'}`}>{s.adjustment > 0 ? '+' : ''}Q{s.adjustment.toFixed(2)}</td></tr>
                  ))}</tbody></table>
                )}
                <button onClick={() => { setShowRecalculateModal(false); setRecalculateResult(null); }} className="w-full py-2.5 bg-blue-600 text-white rounded-xl font-medium text-sm">Cerrar</button>
              </div>
            )}
            {recalculateResult?.error && (
              <div className="space-y-3"><p className="text-sm text-red-600 bg-red-50 p-3 rounded-xl">{recalculateResult.error}</p><button onClick={() => { setShowRecalculateModal(false); setRecalculateResult(null); }} className="w-full py-2.5 bg-slate-100 text-slate-600 rounded-xl text-sm">Cerrar</button></div>
            )}
          </div>
        </div>
      )}

      {/* ═══ HEADER HERO ═══ */}
      <div className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 rounded-2xl p-6 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-72 h-72 bg-blue-500/10 rounded-full -mr-20 -mt-20 blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-400/10 rounded-full -ml-10 -mb-10 blur-2xl"></div>
        
        <div className="relative z-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <p className="text-blue-300 text-sm font-medium" data-testid="dashboard-title">Dashboard</p>
            <h1 className="text-2xl font-bold mt-1">Bienvenido, {user?.name}</h1>
            {user?.store_id && dashboard?.stores?.[0] && (
              <p className="text-slate-400 text-sm mt-1 flex items-center space-x-1">
                <Building2 size={14} />
                <span>{dashboard.stores[0].store_name}</span>
                {user?.role === 'admin' && <button onClick={handleRemoveAdminStore} className="text-red-400 hover:text-red-300 ml-2 text-xs">(Quitar)</button>}
              </p>
            )}
          </div>
          <div className="flex items-center space-x-2">
            {user?.role === 'admin' && (
              <>
                <button onClick={() => setShowFixStoresModal(true)} className="px-3 py-2 text-xs font-medium text-red-300 bg-red-500/15 rounded-lg hover:bg-red-500/25 transition-colors border border-red-500/20 flex items-center space-x-1.5">
                  <UserX size={14} /><span>Corregir Admin</span>
                </button>
                <button onClick={() => setShowRecalculateModal(true)} className="px-3 py-2 text-xs font-medium text-amber-300 bg-amber-500/15 rounded-lg hover:bg-amber-500/25 transition-colors border border-amber-500/20 flex items-center space-x-1.5">
                  <RefreshCw size={14} /><span>Recalcular</span>
                </button>
              </>
            )}
            <button onClick={loadDashboard} className="px-4 py-2 text-xs font-medium text-blue-300 bg-blue-500/15 rounded-lg hover:bg-blue-500/25 transition-colors border border-blue-500/20" data-testid="refresh-dashboard-button">
              Actualizar
            </button>
          </div>
        </div>

        {/* ═══ TODAY'S HIGHLIGHTS ═══ */}
        <div className="relative z-10 grid grid-cols-2 lg:grid-cols-4 gap-3 mt-6">
          <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
            <div className="flex items-center space-x-2 mb-2">
              <div className="bg-green-500/20 rounded-lg p-1.5"><TrendingUp size={14} className="text-green-400" /></div>
              <span className="text-xs text-slate-400 uppercase tracking-wider">Ventas Hoy</span>
            </div>
            <p className="text-xl font-bold text-green-400 tabular-nums">Q{highlights.totalSalesToday.toLocaleString(undefined, {minimumFractionDigits:2})}</p>
          </div>
          <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
            <div className="flex items-center space-x-2 mb-2">
              <div className="bg-red-500/20 rounded-lg p-1.5"><TrendingDown size={14} className="text-red-400" /></div>
              <span className="text-xs text-slate-400 uppercase tracking-wider">Gastos Hoy</span>
            </div>
            <p className="text-xl font-bold text-red-400 tabular-nums">Q{highlights.totalExpensesToday.toLocaleString(undefined, {minimumFractionDigits:2})}</p>
          </div>
          <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
            <div className="flex items-center space-x-2 mb-2">
              <div className="bg-blue-500/20 rounded-lg p-1.5"><Award size={14} className="text-blue-400" /></div>
              <span className="text-xs text-slate-400 uppercase tracking-wider">Top Ventas</span>
            </div>
            <p className="text-sm font-bold text-blue-300 truncate">{highlights.topSalesStore?.name || '-'}</p>
            <p className="text-xs text-slate-500 tabular-nums">{highlights.topSalesStore?.currency || 'Q'}{(highlights.topSalesStore?.amount || 0).toLocaleString(undefined, {minimumFractionDigits:2})}</p>
          </div>
          <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
            <div className="flex items-center space-x-2 mb-2">
              <div className="bg-orange-500/20 rounded-lg p-1.5"><Flame size={14} className="text-orange-400" /></div>
              <span className="text-xs text-slate-400 uppercase tracking-wider">Top Gastos</span>
            </div>
            <p className="text-sm font-bold text-orange-300 truncate">{highlights.topExpenseStore?.name || '-'}</p>
            <p className="text-xs text-slate-500 tabular-nums">{highlights.topExpenseStore?.currency || 'Q'}{(highlights.topExpenseStore?.amount || 0).toLocaleString(undefined, {minimumFractionDigits:2})}</p>
          </div>
        </div>
      </div>

      {/* ═══ BALANCE CARDS ═══ */}
      {isAdmin && dashboard?.admin_reports && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Total by currency */}
          {Object.entries(dashboard.admin_reports.total_balance_by_currency).map(([currency, total]) => (
            <div key={currency} className="bg-white rounded-xl p-5 shadow-md border border-slate-200 hover:shadow-lg transition-all">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Saldo {currency === 'Q' ? 'Quetzales' : 'Dólares'}</span>
                <div className={`rounded-lg p-2 ${currency === 'Q' ? 'bg-emerald-50' : 'bg-blue-50'}`}>
                  <DollarSign size={18} className={currency === 'Q' ? 'text-emerald-600' : 'text-blue-600'} />
                </div>
              </div>
              <p className={`text-3xl font-bold tabular-nums ${total >= 0 ? 'text-slate-800' : 'text-red-600'}`}>
                <span className={`text-lg ${currency === 'Q' ? 'text-emerald-500' : 'text-blue-500'}`}>{currency}</span>
                {total.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
              </p>
            </div>
          ))}

          {/* Stores summary */}
          <div className="bg-white rounded-xl p-5 shadow-md border border-slate-200 lg:col-span-1">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Por Tienda</span>
              <div className="bg-slate-100 rounded-lg p-2"><Building2 size={18} className="text-slate-600" /></div>
            </div>
            <div className="space-y-1.5 max-h-32 overflow-y-auto">
              {dashboard.admin_reports.stores_summary.map((store) => (
                <div key={store.store_name} className="flex justify-between items-center py-1.5 px-2 rounded-lg hover:bg-slate-50 transition-colors">
                  <span className="text-xs text-slate-600 truncate mr-2">{store.store_name}</span>
                  <span className={`text-xs font-bold tabular-nums whitespace-nowrap ${store.balance >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                    {store.currency}{store.balance.toLocaleString(undefined, {minimumFractionDigits: 2})}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ═══ MÉTODOS DE PAGO ═══ */}
      {isAdminOrManager && salesReport && (
        <div className="space-y-4">
          {/* Filters bar */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <h2 className="text-lg font-bold text-slate-800 flex items-center space-x-2">
              <BarChart3 size={20} className="text-blue-600" />
              <span>Ventas por Método de Pago</span>
              <span className="text-sm font-normal text-slate-400">— {monthsFull[reportFilters.month]} {reportFilters.year}</span>
            </h2>
            <div className="flex items-center gap-2">
              <select value={reportFilters.store_id} onChange={(e) => setReportFilters({...reportFilters, store_id: e.target.value})} className="text-sm bg-white border border-slate-200 text-slate-600 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-100 shadow-sm">
                <option value="">Todas</option>
                {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              <select value={reportFilters.month} onChange={(e) => setReportFilters({...reportFilters, month: parseInt(e.target.value)})} className="text-sm bg-white border border-slate-200 text-slate-600 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-100 shadow-sm">
                <option value="">Año</option>
                {months.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
              <select value={reportFilters.year} onChange={(e) => setReportFilters({...reportFilters, year: parseInt(e.target.value)})} className="text-sm bg-white border border-slate-200 text-slate-600 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-100 shadow-sm">
                {years.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>

          {/* KPI + Methods grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Left: KPIs */}
            <div className="space-y-4">
              <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl p-5 text-white shadow-lg">
                <p className="text-blue-200 text-xs uppercase tracking-wider mb-1">Total Ventas</p>
                <p className="text-3xl font-bold tabular-nums">Q{salesReport.totals.total_amount.toLocaleString()}</p>
                <div className="flex items-center space-x-4 mt-3 text-blue-200 text-xs">
                  <span className="flex items-center space-x-1"><Hash size={12} /><span>{salesReport.totals.transaction_count} transacciones</span></span>
                  <span>Prom. Q{salesReport.totals.average_per_sale.toFixed(0)}</span>
                </div>
              </div>

              {/* Payment method cards */}
              <div className="grid grid-cols-1 gap-2">
                {Object.entries(salesReport.by_payment_method).map(([method, data]) => {
                  const Icon = PAYMENT_ICONS[method] || Receipt;
                  const color = PAYMENT_COLORS[method] || '#94a3b8';
                  const pct = salesReport.totals.total_amount > 0 ? (data.total_amount / salesReport.totals.total_amount * 100) : 0;
                  return (
                    <div key={method} className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 hover:shadow-md transition-all">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="rounded-lg p-2" style={{ backgroundColor: `${color}15` }}>
                            <Icon size={16} style={{ color }} />
                          </div>
                          <div>
                            <p className="font-semibold text-slate-800 text-sm">{method}</p>
                            <p className="text-xs text-slate-400">{data.transaction_count} trans.</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold tabular-nums text-slate-800">Q{data.total_amount.toLocaleString()}</p>
                          <p className="text-xs font-medium" style={{ color }}>{pct.toFixed(1)}%</p>
                        </div>
                      </div>
                      {/* Progress bar */}
                      <div className="mt-2 bg-slate-100 rounded-full h-1.5 overflow-hidden">
                        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: color }}></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Center: Pie chart */}
            <div className="bg-white rounded-xl p-5 shadow-md border border-slate-200">
              <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center space-x-2">
                <PieChart className="text-blue-500" size={16} />
                <span>Distribución</span>
              </h3>
              {getPieChartData().length > 0 ? (
                <ResponsiveContainer width="100%" height={320}>
                  <RechartsPieChart>
                    <Pie data={getPieChartData()} cx="50%" cy="50%" labelLine={false} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} outerRadius={100} innerRadius={50} fill="#8884d8" dataKey="value" strokeWidth={3} stroke="#fff">
                      {getPieChartData().map((entry, index) => <Cell key={`cell-${index}`} fill={PAYMENT_COLORS[entry.name] || '#94a3b8'} />)}
                    </Pie>
                    <Tooltip formatter={(value) => [`Q${value.toLocaleString()}`, 'Total']} contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px' }} />
                  </RechartsPieChart>
                </ResponsiveContainer>
              ) : <div className="h-80 flex items-center justify-center text-slate-300 text-sm">Sin datos</div>}
            </div>

            {/* Right: Bar chart */}
            <div className="bg-white rounded-xl p-5 shadow-md border border-slate-200">
              <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center space-x-2">
                <BarChart3 className="text-blue-500" size={16} />
                <span>Comparativa</span>
              </h3>
              {getBarChartData().some(d => d.total > 0) ? (
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={getBarChartData()} layout="vertical">
                    <XAxis type="number" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} width={70} />
                    <Tooltip formatter={(value) => [`Q${value.toLocaleString()}`, 'Total']} contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px' }} />
                    <Bar dataKey="total" fill="#2563eb" radius={[0, 6, 6, 0]} barSize={24} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <div className="h-80 flex items-center justify-center text-slate-300 text-sm">Sin datos</div>}
            </div>
          </div>
        </div>
      )}

      {/* ═══ NEW CHARTS SECTION ═══ */}
      {['admin', 'manager', 'supervisor'].includes(user?.role) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Store Comparison Chart */}
          <div className="bg-white rounded-xl shadow-md border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-slate-800 flex items-center space-x-2">
                <BarChart3 size={18} className="text-blue-600" />
                <span>Comparativa entre Tiendas</span>
              </h3>
              <div className="flex space-x-1">
                {[
                  { value: 'today', label: 'Hoy' },
                  { value: 'week', label: 'Semana' },
                  { value: 'month', label: 'Mes' }
                ].map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setComparisonPeriod(opt.value)}
                    className={`px-3 py-1 text-xs font-semibold rounded-lg transition-all ${
                      comparisonPeriod === opt.value
                        ? 'bg-blue-600 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            {storeComparison?.stores?.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart
                  data={storeComparison.stores.map(s => ({
                    name: s.store_name.length > 12 ? s.store_name.substring(0, 12) + '...' : s.store_name,
                    fullName: s.store_name,
                    ventas: s.sales,
                    gastos: s.expenses,
                    neto: s.net,
                    currency: s.currency
                  }))}
                  margin={{ top: 10, right: 10, left: 0, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#64748b' }} />
                  <YAxis tick={{ fontSize: 10, fill: '#64748b' }} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                  <Tooltip
                    formatter={(value, name) => [`Q${value.toLocaleString()}`, name === 'ventas' ? 'Ventas' : name === 'gastos' ? 'Gastos' : 'Neto']}
                    labelFormatter={(label, payload) => payload?.[0]?.payload?.fullName || label}
                    contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px' }}
                  />
                  <Legend wrapperStyle={{ fontSize: '11px' }} />
                  <Bar dataKey="ventas" name="Ventas" fill="#10b981" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="gastos" name="Gastos" fill="#ef4444" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-64 flex items-center justify-center text-slate-400 text-sm">
                Sin datos para el período seleccionado
              </div>
            )}
          </div>

          {/* Trends Chart */}
          <div className="bg-white rounded-xl shadow-md border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-slate-800 flex items-center space-x-2">
                <LineChart size={18} className="text-emerald-600" />
                <span>Tendencias de Ventas</span>
              </h3>
              <div className="flex space-x-1">
                {[
                  { value: 'weekly', label: 'Semanal' },
                  { value: 'monthly', label: 'Mensual' }
                ].map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setTrendsPeriod(opt.value)}
                    className={`px-3 py-1 text-xs font-semibold rounded-lg transition-all ${
                      trendsPeriod === opt.value
                        ? 'bg-emerald-600 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            {salesTrends?.data?.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <RechartsLineChart
                  data={salesTrends.data.map(d => ({
                    ...d,
                    label: d.label + (d.day ? ` ${d.day}` : '')
                  }))}
                  margin={{ top: 10, right: 10, left: 0, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#64748b' }} />
                  <YAxis tick={{ fontSize: 10, fill: '#64748b' }} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                  <Tooltip
                    formatter={(value, name) => [`Q${value.toLocaleString()}`, name === 'sales' ? 'Ventas' : 'Gastos']}
                    contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px' }}
                  />
                  <Legend wrapperStyle={{ fontSize: '11px' }} formatter={v => v === 'sales' ? 'Ventas' : 'Gastos'} />
                  <Line type="monotone" dataKey="sales" name="sales" stroke="#10b981" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                  <Line type="monotone" dataKey="expenses" name="expenses" stroke="#ef4444" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                </RechartsLineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-64 flex items-center justify-center text-slate-400 text-sm">
                Sin datos para el período seleccionado
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══ STORE CARDS ═══ */}
      <div>
        <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center space-x-2">
          <Store size={20} className="text-blue-600" />
          <span>Tiendas</span>
          <span className="text-sm font-normal text-slate-400">({dashboard?.stores?.length || 0})</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {dashboard?.stores?.map((store) => (
            <div key={store.store_id} className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden hover:shadow-lg transition-all group" data-testid={`store-card-${store.store_id}`}>
              {/* Card header */}
              <div className="bg-gradient-to-r from-slate-800 to-slate-700 px-5 py-3.5 flex items-center space-x-3 group-hover:from-blue-800 group-hover:to-blue-700 transition-all">
                <div className="bg-white/10 rounded-lg p-1.5">
                  <Building2 className="text-white" size={16} />
                </div>
                <h3 className="text-sm font-bold text-white truncate">{store.store_name}</h3>
              </div>
              
              <div className="p-5 space-y-4">
                {/* Saldo */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="bg-emerald-50 rounded-lg p-1.5"><DollarSign size={14} className="text-emerald-600" /></div>
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Saldo Real</span>
                  </div>
                  <span className={`text-lg font-bold tabular-nums ${store.balance_without_petty >= 0 ? 'text-emerald-600' : 'text-red-600'}`} data-testid="current-balance">
                    {store.currency}{store.balance_without_petty.toLocaleString(undefined, {minimumFractionDigits: 2})}
                  </span>
                </div>

                {/* Fondo Sencillo */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="bg-blue-50 rounded-lg p-1.5"><Wallet size={14} className="text-blue-600" /></div>
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Fondo Sencillo</span>
                  </div>
                  <span className="text-lg font-bold text-blue-600 tabular-nums" data-testid="petty-cash-fund">
                    {store.currency}{store.petty_cash_fund.toLocaleString(undefined, {minimumFractionDigits: 2})}
                  </span>
                </div>

                {/* Divider + Today */}
                <div className="border-t border-slate-100 pt-3">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Actividad de Hoy</p>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="bg-green-50 rounded-lg py-2 px-1">
                      <ArrowUpRight size={12} className="text-green-500 mx-auto mb-0.5" />
                      <p className="text-xs font-bold text-green-600 tabular-nums">{store.currency}{store.total_sales_today.toLocaleString(undefined, {minimumFractionDigits: 0})}</p>
                      <p className="text-[9px] text-green-500">Ventas</p>
                    </div>
                    <div className="bg-red-50 rounded-lg py-2 px-1">
                      <ArrowDownRight size={12} className="text-red-400 mx-auto mb-0.5" />
                      <p className="text-xs font-bold text-red-500 tabular-nums">{store.currency}{store.total_expenses_today.toLocaleString(undefined, {minimumFractionDigits: 0})}</p>
                      <p className="text-[9px] text-red-400">Gastos</p>
                    </div>
                    <div className="bg-blue-50 rounded-lg py-2 px-1">
                      <Activity size={12} className="text-blue-500 mx-auto mb-0.5" />
                      <p className={`text-xs font-bold tabular-nums ${store.net_today >= 0 ? 'text-blue-600' : 'text-red-500'}`}>{store.currency}{store.net_today.toLocaleString(undefined, {minimumFractionDigits: 0})}</p>
                      <p className="text-[9px] text-blue-400">Neto</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {dashboard?.stores?.length === 0 && (
        <div className="bg-slate-50 rounded-xl p-12 text-center border border-slate-200">
          <Building2 size={36} className="text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No hay tiendas disponibles.</p>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
